import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import { Upload, Save, FileSpreadsheet } from 'lucide-react';

const HEADER_FIELDS = [
  { key: 'requesterName', label: 'Employee Name', hint: 'e.g. C6' },
  { key: 'reportDate', label: 'Report Date', hint: 'e.g. G6' },
  { key: 'officeLocation', label: 'Office Location', hint: 'e.g. C7' },
  { key: 'position', label: 'Position', hint: 'e.g. G7' },
  { key: 'claimPurpose', label: 'Claim Purpose', hint: 'e.g. C8' },
  { key: 'claimRef', label: 'Claim Reference', hint: 'e.g. G8' },
  { key: 'project', label: 'Project', hint: 'e.g. C9' },
  { key: 'client', label: 'Client', hint: 'e.g. G9' },
  { key: 'mileageRate', label: 'Mileage Rate', hint: 'e.g. J8' },
  { key: 'totalClaimedSAR', label: 'Total Claimed (SAR)', hint: 'e.g. J22' },
  { key: 'lessAdvancesPaid', label: 'Less Advances Paid', hint: 'e.g. J23' },
  { key: 'netAmount', label: 'Net Amount', hint: 'e.g. J24' },
];

const LINE_ITEM_FIELDS = [
  { key: 'lineNumber', label: 'Line #' },
  { key: 'date', label: 'Date' },
  { key: 'expenseType', label: 'Expense Type' },
  { key: 'description', label: 'Description' },
  { key: 'projectPermitted', label: 'Project Permitted' },
  { key: 'chargeAccount', label: 'Charge Account' },
  { key: 'currencyType', label: 'Currency' },
  { key: 'currencyAmount', label: 'Amount (Original)' },
  { key: 'exchangeRate', label: 'Exchange Rate' },
  { key: 'originalAmount', label: 'Amount Before Exchange' },
  { key: 'sarAmount', label: 'Amount (SAR)' },
];

export default function ExpenseTemplatePage() {
  const toast = useToast();
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [cellMappings, setCellMappings] = useState({});
  const [lineItemStartRow, setLineItemStartRow] = useState(12);
  const [lineItemEndRow, setLineItemEndRow] = useState(21);
  const [lineItemMappings, setLineItemMappings] = useState({});
  const [signatureName, setSignatureName] = useState('');
  const [signatureDate, setSignatureDate] = useState('');

  useEffect(() => {
    adminApi.getExpenseTemplateMapping()
      .then((res) => {
        setFileName(res.templateFileName || '');
        if (res.mapping) {
          const cm = res.mapping.cellMappings || {};
          const reversed = {};
          for (const [ref, field] of Object.entries(cm)) reversed[field] = ref;
          setCellMappings(reversed);
          setLineItemStartRow(res.mapping.lineItemStartRow || 12);
          setLineItemEndRow(res.mapping.lineItemEndRow || 21);
          setLineItemMappings(res.mapping.lineItemMappings || {});
          if (res.mapping.signatureName) setSignatureName(res.mapping.signatureName);
          if (res.mapping.signatureDate) setSignatureDate(res.mapping.signatureDate);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('template', file);
      await adminApi.uploadExpenseTemplate(formData);
      setFileName(file.name);
      toast.success('Template uploaded');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cm = {};
      for (const [field, ref] of Object.entries(cellMappings)) {
        if (ref?.trim()) cm[ref.trim().toUpperCase()] = field;
      }
      if (signatureName.trim()) cm[signatureName.trim().toUpperCase()] = 'requesterName';
      if (signatureDate.trim()) cm[signatureDate.trim().toUpperCase()] = 'reportDate';

      const cleanLine = {};
      for (const [k, v] of Object.entries(lineItemMappings)) { if (v) cleanLine[k] = v; }

      await adminApi.saveExpenseTemplateMapping({
        mapping: {
          cellMappings: cm,
          lineItemStartRow,
          lineItemEndRow,
          lineItemMappings: cleanLine,
          signatureName: signatureName.trim().toUpperCase(),
          signatureDate: signatureDate.trim().toUpperCase(),
        },
      });
      toast.success('Mapping saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><div className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-border border-t-primary" /></div>;
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Export Template</h1>
        <p className="text-muted-foreground">Configure the XLSX template for expense report exports</p>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-primary" />
              Template File
            </CardTitle>
            <CardDescription>{fileName ? `Current: ${fileName}` : 'Upload your company XLSX template'}</CardDescription>
          </CardHeader>
          <CardContent>
            <label className="flex items-center gap-3 rounded-lg border border-dashed border-border px-4 py-4 cursor-pointer hover:border-primary/30 hover:bg-muted/30 transition-all">
              <Upload className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-foreground">{uploading ? 'Uploading...' : 'Upload XLSX template'}</p>
                <p className="text-xs text-muted-foreground">Replaces current template</p>
              </div>
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} disabled={uploading} />
            </label>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Report Fields</CardTitle>
            <CardDescription>Type the cell reference from your template where each field goes. Open your template in Excel to find the cells.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {HEADER_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-36 shrink-0">{f.label}</span>
                  <input
                    value={cellMappings[f.key] || ''}
                    onChange={(e) => setCellMappings((m) => ({ ...m, [f.key]: e.target.value }))}
                    placeholder={f.hint}
                    className="w-20 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs font-mono uppercase text-center focus:border-primary/40 focus:outline-none transition-colors placeholder:text-muted-foreground/30 placeholder:normal-case"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Line Items</CardTitle>
            <CardDescription>Set the row range and map each column to a field. Extra rows are added automatically for reports with more items.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-end gap-3">
              <Input label="Start Row" type="number" min="1" value={lineItemStartRow} onChange={(e) => setLineItemStartRow(parseInt(e.target.value) || 12)} className="w-24" />
              <Input label="End Row" type="number" min="1" value={lineItemEndRow} onChange={(e) => setLineItemEndRow(parseInt(e.target.value) || 21)} className="w-24" />
              <span className="text-xs text-muted-foreground pb-2">{lineItemEndRow - lineItemStartRow + 1} rows</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'].map((col) => (
                <div key={col} className="flex items-center gap-2">
                  <span className="font-mono text-xs font-semibold text-primary w-6">{col}</span>
                  <select
                    value={lineItemMappings[col] || ''}
                    onChange={(e) => setLineItemMappings((m) => {
                      const copy = { ...m };
                      if (e.target.value) copy[col] = e.target.value;
                      else delete copy[col];
                      return copy;
                    })}
                    className="flex-1 bg-card border border-border rounded-md px-2 py-1.5 text-xs focus:border-primary/40 focus:outline-none"
                  >
                    <option value="">— Not mapped —</option>
                    {LINE_ITEM_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Signature Area</CardTitle>
            <CardDescription>Optional — cells where employee name and date repeat for the signature block</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-36 shrink-0">Employee Name</span>
                <input value={signatureName} onChange={(e) => setSignatureName(e.target.value)} placeholder="e.g. D27"
                  className="w-20 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs font-mono uppercase text-center focus:border-primary/40 focus:outline-none transition-colors placeholder:text-muted-foreground/30 placeholder:normal-case" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-36 shrink-0">Date</span>
                <input value={signatureDate} onChange={(e) => setSignatureDate(e.target.value)} placeholder="e.g. I27"
                  className="w-20 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs font-mono uppercase text-center focus:border-primary/40 focus:outline-none transition-colors placeholder:text-muted-foreground/30 placeholder:normal-case" />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} loading={saving} disabled={saving}>
            <Save className="h-4 w-4" /> Save Mapping
          </Button>
        </div>
      </div>
    </div>
  );
}
